import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import {
  SharedImageComponent,
  SharedImageComponentSchema,
} from "@intelligems/ig-types";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import { getDefaultImage } from "@src/store/slices/experienceBuilder/component/defaults";
import {
  ImageComponent,
  ModHandler,
} from "@src/store/slices/experienceBuilder/types";

import { escapeAttr as esc } from "./escapeAttr";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";

function toHtml(mod: ImageComponent): string {
  return `<ig-image-component
    src="${esc(mod.src)}"
    alt="${esc(mod.alt)}"
    width="${esc(mod.width)}"
    max-height="${esc(mod.maxHeight)}"
    scaling="${esc(mod.scaling)}"
    object-position="${esc(mod.objectPosition)}"
  ></ig-image-component>`;
}

function fromHtml(html: string): Partial<ImageComponent> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const el = doc.querySelector("ig-image-component");
  if (!el) return {};
  return {
    src: el.getAttribute("src") ?? "",
    alt: el.getAttribute("alt") ?? "",
    width: el.getAttribute("width") ?? "100%",
    maxHeight: el.getAttribute("max-height") ?? "",
    scaling:
      (el.getAttribute("scaling") as ImageComponent["scaling"]) ?? "contain",
    objectPosition:
      (el.getAttribute(
        "object-position"
      ) as ImageComponent["objectPosition"]) ?? "center",
  };
}

export const imageComponentHandler: ModHandler<ImageComponent> = {
  normalize: (mod) => mod,

  convertFrom: (mod) => ({
    type: "onsiteEdit",
    dto: {
      id: mod.id,
      variationId: mod.variationId,
      querySelectors: [mod.selector],
      onsiteEditType: "html",
      replace: toHtml(mod),
      find: "",
      isRegex: false,
      skip: false,
      remove: false,
      isHtml: false,
      isImage: false,
      insertType: mod.insertType === "replace" ? null : mod.insertType,
      createdAtUrl: mod.createdAtUrl,
      source: mod.source ?? null,
      title: mod.description ?? null,
    },
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteEdit")
      throw new Error("Expected onsiteEdit");
    const edit = conversion.dto;
    const parsed = fromHtml(edit.replace);
    return {
      type: "imageComponent",
      category: "component",
      id: edit.id,
      variationId: edit.variationId,
      selector: edit.querySelectors.join(","),
      insertType: edit.insertType ?? "replace",
      createdAtUrl: edit.createdAtUrl ?? undefined,
      source: edit.source ?? undefined,
      src: parsed.src ?? "",
      alt: parsed.alt ?? "",
      width: parsed.width ?? "100%",
      maxHeight: parsed.maxHeight ?? "",
      scaling: parsed.scaling ?? "contain",
      objectPosition: parsed.objectPosition ?? "center",
      description: edit.title ?? undefined,
    };
  },

  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    if (!mod.src) {
      errors.src = "Image URL is required";
    }
    if (!mod.selector) {
      errors.selector = "Element selection is required";
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    return errors;
  },

  isEquivalent: (a, b) =>
    b.type === "imageComponent" &&
    a.selector === b.selector &&
    a.insertType === b.insertType,

  defaults: () =>
    getDefaultImage({
      id: v4(),
      variationId: "",
      selector: "",
      insertType: "beforebegin",
    }),

  toAiFormat: (mod) =>
    pickSharedFields<SharedImageComponent>(SharedImageComponentSchema, mod),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? imageComponentHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod, newMod) => {
    const pick = (mod: ImageComponent) => ({
      src: mod.src,
      alt: mod.alt,
      width: mod.width,
      maxHeight: mod.maxHeight,
      scaling: mod.scaling,
      objectPosition: mod.objectPosition,
    });
    return !isEqual(pick(oldMod), pick(newMod));
  },
};
